import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApService } from 'src/app/shared/services/ap.service';
import { TemplateService } from 'src/app/shared/services/template.service';
import { VceVhubService } from 'src/app/shared/services/vce-vhub.service';
import { MonitorCapabilities } from './tabs/device-monitor-tabs';

export interface MonitorSnapshot {
  // Existing feature components consume the legacy API's device-array contract.
  readonly devices: any[];
  readonly ras: any;
  readonly capabilities: MonitorCapabilities;
  readonly lan1Active: boolean;
  readonly lan2Active: boolean;
}

export function isLanJumpActive(jump: any, now = Date.now()): boolean {
  return Boolean(jump && Number(jump.enabled) === 1 &&
    (Number(jump.applied_time) * 1000 + Number(jump.expire_time) * 60000) > now);
}

export function monitorCapabilities(device: any, profile: any, ras: any, role: number): MonitorCapabilities {
  const config = device && device.templateConfig || {};
  const source = profile || device || {};
  const hasRas = Boolean(ras && (ras.ras_group != null || ras.ras_asset != null ||
    ras.ras_policy != null || ras.wg0 && ras.wg0.peers != null));
  return {
    diagnostics: [0, 1, 2, 3].indexOf(role) !== -1,
    team: Boolean(source.team || !profile && config.team && config.team.config),
    qos: Boolean(source.qos || !profile && config.qos && config.qos.config && config.qos.config.length),
    wireless: Boolean(source.wnetwork || !profile && config.wnetwork && config.wnetwork.config),
    lte: Boolean(source['4glte'] || !profile && config['4glte'] && config['4glte'].config),
    statistics: Boolean(device && device.statistics),
    jump: Boolean(config.jump),
    ras: config.team?.config?.team1?.mode === 'VCE' && hasRas
  };
}

@Injectable({ providedIn: 'root' })
export class DeviceMonitorDataService {
  constructor(private readonly apService: ApService, private readonly templateService: TemplateService,
    private readonly vceVhubService: VceVhubService) {}

  /** One cancellable snapshot prevents late profile/RAS responses from changing another device. */
  load(deviceId: string, role: number): Observable<MonitorSnapshot> {
    return this.apService.getdeviceById(deviceId).pipe(switchMap(response => {
      const devices = response && response.data || [];
      const device = devices[0];
      const profile$ = device && device.tprofile
        ? this.templateService.getTprofile(device.tprofile).pipe(
          map(profiles => profiles && profiles[0] || null), catchError(() => of(null)))
        : of(null);
      const ras$ = device && device.mac
        ? this.vceVhubService.getRas(device.mac).pipe(catchError(() => of(null))) : of(null);
      return forkJoin({ profile: profile$, ras: ras$ }).pipe(map(({ profile, ras }) => {
        const merged = device ? { ...device } : null;
        if (merged && profile) {
          ['qos', 'alertId', 'wnetwork', '4glte', 'team'].forEach(key => {
            if (profile[key]) merged[key] = profile[key];
          });
        }
        return {
          devices: merged ? [merged, ...devices.slice(1)] : [], ras,
          capabilities: monitorCapabilities(device, profile, ras, role),
          lan1Active: isLanJumpActive(device?.templateConfig?.jump?.landevice1),
          lan2Active: isLanJumpActive(device?.templateConfig?.jump?.landevice2)
        };
      }));
    }));
  }
}
